import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import feedbackRequestApi from "../../../../api/feedback/feedbackRequestApi";
import { MultipleChoiceOptionStringId } from "../../../../types/forms/MultipleChoiceOptions";
import {
  getSelectedDeclineOption,
} from "./feedbackDeclineOptions";
import { normalizeFeedbackRequestError } from "./normalizeFeedbackRequestError";

interface UseFeedbackDeclineSubmitOptions {
  api: feedbackRequestApi;
  requestId: number | null | undefined;
  onSaved: () => void;
  onWillGive: () => void;
}

export function useFeedbackDeclineSubmit({
  api,
  requestId,
  onSaved,
  onWillGive,
}: UseFeedbackDeclineSubmitOptions) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const submitDecline = useCallback(
    (declineOptions: MultipleChoiceOptionStringId[]) => {
      const selectedOption = getSelectedDeclineOption(declineOptions);
      if (!selectedOption || requestId == null) {
        return;
      }

      if (selectedOption.value === "WILL_GIVE") {
        onWillGive();
        return;
      }

      setIsSaving(true);
      setSaveError(null);

      api.saveNote(
        requestId,
        { body: selectedOption.text },
        () => {
          setIsSaving(false);
          onSaved();
        },
        (err: unknown) => {
          setIsSaving(false);
          setSaveError(
            normalizeFeedbackRequestError(
              err,
              t("Feedback.Decline.DeclineSaveFailed", {
                defaultValue: "Something went wrong. Please try again.",
              })
            )
          );
        }
      );
    },
    [api, onSaved, onWillGive, requestId, t]
  );

  const clearSaveError = useCallback(() => setSaveError(null), []);

  return {
    isSaving,
    saveError,
    submitDecline,
    clearSaveError,
    setSaveError,
  };
}

export default useFeedbackDeclineSubmit;
